import { useState, useCallback, useEffect, useRef, memo } from "react";
import { ActivityIndicator } from "react-native";
import { View } from "@src/components";
import { useVideoPlayer } from "expo-video";
import { useSharedValue, useAnimatedStyle, withTiming, useAnimatedReaction, Easing } from "react-native-reanimated";
import type { Clip } from "../types/clip";
import { VideoProgressBar } from "./VideoProgressBar";
import { NoClipPlaceholder } from "./NoClipPlaceholder";
import { ClipInfoHeader } from "./ClipInfoHeader";
import { VideoThumbnailOverlay } from "./VideoThumbnailOverlay";
import { VideoControlsOverlay } from "./VideoControlsOverlay";
import { VideoPlayerView, type VideoViewRef } from "./VideoPlayerView";
import { ExitFullscreenButton } from "./ExitFullscreenButton";
import { AnimatedFullscreenButton } from "./AnimatedFullscreenButton";
import { AnimatedMuteButton } from "./AnimatedMuteButton";
import { VideoLoadingOverlay } from "./VideoLoadingOverlay";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { trackEvent } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

interface ClipVideoPlayerProps {
  clip: Clip | null;
  isLoading?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  screenHeight: number;
  screenWidth: number;
  clipIndex?: number;
  totalClips?: number;
  orientation?: "portrait" | "landscape";
  selectedHour: number;
  /**
   * Autoplay muted on load. Defaults to true (the clips page). Set false to
   * show the first frame paused — used by dumping search, where a rapidly
   * scanned clip should hold still until the reviewer chooses to play.
   */
  autoPlay?: boolean;
  onViewed?: (clip: Clip) => void;
  /**
   * Opt-in override for the clip's max height cap (px). When omitted the player
   * caps itself at 40% of the window height (the clips-page default). Dumping
   * Search passes the measured height of its available area so the clip grows
   * to fill it (still 16:9, centered) instead of being pinned to that 40%.
   */
  maxHeightOverride?: number;
}

function formatHourRange(hour: number): string {
  const formatHour = (h: number): string => {
    if (h === 0 || h === 24) {
      return "12:00am";
    }
    if (h < 12) {
      return `${h}:00am`;
    }
    if (h === 12) {
      return "12:00pm";
    }
    return `${h - 12}:00pm`;
  };

  const startHour = formatHour(hour);
  const endHour = formatHour(hour + 1);
  return `${startHour} - ${endHour}`;
}

export const ClipVideoPlayer = memo(
  ({
    clip,
    isLoading = false,
    onPrevious,
    onNext,
    hasPrevious = false,
    hasNext = false,
    screenHeight,
    screenWidth,
    clipIndex,
    totalClips,
    orientation = "portrait",
    selectedHour,
    autoPlay = true,
    onViewed,
    maxHeightOverride,
  }: ClipVideoPlayerProps) => {
    const { theme } = useUnistyles();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const videoViewRef = useRef<VideoViewRef>(null);
    const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const viewedClipIdRef = useRef<string | null>(null);

    const isLandscape = orientation === "landscape";

    // Controls fade animation
    const controlsOpacity = useSharedValue(0);

    // Sync controlsVisible state with opacity animation
    useAnimatedReaction(
      () => controlsVisible || isFullscreen,
      (shouldShow) => {
        controlsOpacity.value = withTiming(shouldShow ? 1 : 0, {
          duration: 150,
          easing: Easing.out(Easing.ease),
        });
      },
      [controlsVisible, isFullscreen],
    );

    const controlsAnimatedStyle = useAnimatedStyle(() => ({
      opacity: controlsOpacity.value,
    }));

    const player = useVideoPlayer(clip?.videoUrl || null, (player) => {
      player.loop = false;
      // Clips always start muted; the user can opt in to audio per-clip via the mute toggle.
      player.muted = true;
      if (autoPlay) player.play();
    });

    const [isPlaying, setIsPlaying] = useState(player.playing);
    const [isEnded, setIsEnded] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Reset states when clip changes
    useEffect(() => {
      setVideoLoaded(false);
      setIsPlaying(false);
      setIsEnded(false);
      // Re-mute on clip change. The useVideoPlayer setup callback already re-mutes the
      // underlying player, but mutedChange won't fire if the previous clip was also muted,
      // so we need to reset the local state explicitly to keep the icon in sync.
      setIsMuted(true);
      setControlsVisible(false);
      setCurrentTime(0);
      setDuration(0);
      viewedClipIdRef.current = null;
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, [clip?.id]);

    // Play video when clip changes
    useEffect(() => {
      if (!autoPlay) return;
      if (clip?.videoUrl && player) {
        // Play after a short delay to ensure player source is updated and ready
        const timeoutId = setTimeout(() => {
          /* console.log("ClipVideoPlayer: attempting play", {
          id: clip?.id,
          url: clip?.videoUrl,
          isPlaying: player.playing,
        }); */
          if (!player.playing) {
            try {
              player.play();
            } catch (error: unknown) {
              console.warn("Failed to play video:", error);
            }
          }
        }, 300);
        return () => clearTimeout(timeoutId);
      }
    }, [clip?.id, clip?.videoUrl, player, autoPlay]);

    useEffect(() => {
      const playingSubscription = player.addListener("playingChange", ({ isPlaying }) => {
        setIsPlaying(isPlaying);
      });
      const endSubscription = player.addListener("playToEnd", () => {
        setIsEnded(true);
        setControlsVisible(true);
      });
      const mutedSubscription = player.addListener("mutedChange", ({ muted }) => {
        setIsMuted(muted);
      });
      return () => {
        playingSubscription.remove();
        endSubscription.remove();
        mutedSubscription.remove();
      };
    }, [player]);

    // Track playback progress
    useEffect(() => {
      // Update duration when it becomes available
      if (player.duration > 0) {
        setDuration(player.duration);
      }

      // Poll for current time while playing
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const updateProgress = () => {
        if (player.currentTime >= 0) {
          setCurrentTime(player.currentTime);
        }
        if (player.duration > 0 && duration !== player.duration) {
          setDuration(player.duration);
        }
      };

      // Update immediately
      updateProgress();

      // Poll every 100ms for smooth progress updates
      intervalId = setInterval(updateProgress, 100);

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [player, duration]);

    const handlePlayPause = useCallback(() => {
      if (isEnded) {
        // Restart from beginning
        player.currentTime = 0;
        setIsEnded(false);
        player.play();
      } else if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    }, [isPlaying, isEnded, player]);

    const handleMuteToggle = useCallback(() => {
      const next = !isMuted;
      player.muted = next;
      trackEvent(next ? ANALYTICS_EVENTS.CLIP_MUTED : ANALYTICS_EVENTS.CLIP_UNMUTED, {
        clip_id: clip?.id,
      });
      // Keep the controls visible long enough for the user to see the icon flip — mirrors
      // the auto-hide behaviour in handleVideoTap. Skipped in fullscreen, which has its
      // own native chrome and doesn't auto-hide.
      if (!isFullscreen) {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
        hideControlsTimeoutRef.current = setTimeout(() => {
          setControlsVisible(false);
          hideControlsTimeoutRef.current = null;
        }, 3000);
      }
    }, [isMuted, player, clip?.id, isFullscreen]);

    const handleFullscreen = useCallback(async () => {
      if (videoViewRef.current) {
        if (isFullscreen) {
          // Exit fullscreen - let VideoView handle it naturally
          try {
            if (typeof videoViewRef.current.exitFullscreen === "function") {
              await videoViewRef.current.exitFullscreen();
            } else {
              await videoViewRef.current.enterFullscreen();
            }
          } catch (error) {
            console.warn("Failed to exit fullscreen:", error);
          }
        } else {
          // Enter fullscreen
          await videoViewRef.current.enterFullscreen();
        }
      }
    }, [isFullscreen]);

    const handleFullscreenEnter = useCallback(() => {
      setIsFullscreen(true);
      setControlsVisible(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    const handleFullscreenExit = useCallback(() => {
      setIsFullscreen(false);
      setControlsVisible(false);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    // Toggle controls on tap
    const handleVideoTap = useCallback(() => {
      // Clear existing timeout
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }

      // Toggle visibility
      const newVisible = !controlsVisible;
      // console.log("ClipVideoPlayer: Video tapped, toggling controls", { isFullscreen, controlsVisible, newVisible });
      setControlsVisible(newVisible);

      // In fullscreen, don't auto-hide. Otherwise, auto-hide after 3 seconds if showing
      if (newVisible && !isFullscreen) {
        hideControlsTimeoutRef.current = setTimeout(() => {
          // console.log("ClipVideoPlayer: Hiding controls after timeout");
          setControlsVisible(false);
          hideControlsTimeoutRef.current = null;
        }, 3000);
      }
    }, [isFullscreen, controlsVisible]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
      };
    }, []);

    const handleFirstFrameRender = useCallback(() => {
      setVideoLoaded(true);
      if (clip && viewedClipIdRef.current !== clip.id) {
        viewedClipIdRef.current = clip.id;
        onViewed?.(clip);
      }
    }, [clip, onViewed]);

    if (!clip) {
      if (isLoading) {
        return (
          <>
            <View mb="3xl" mt="lg" />
            <View
              style={[
                styles.loadingContainer,
                {
                  width: screenWidth - theme.spacing.lg * 2,
                  maxHeight: maxHeightOverride ?? screenHeight * 0.4,
                  // Match videoContainer: cap width so the 16:9 box never
                  // exceeds the height cap (which would otherwise clip it).
                  maxWidth: (maxHeightOverride ?? screenHeight * 0.4) * (16 / 9),
                },
              ]}
              alignItems="center"
              justifyContent="center"
            >
              <ActivityIndicator size="large" color={theme.colors.text as string} />
            </View>
          </>
        );
      }
      return (
        <NoClipPlaceholder
          screenWidth={screenWidth}
          screenHeight={screenHeight}
          hourRangeLabel={formatHourRange(selectedHour)}
        />
      );
    }

    return (
      <View style={styles.container}>
        {/* Clip info - below video */}
        {!isLandscape && clipIndex !== undefined && totalClips !== undefined && (
          <ClipInfoHeader
            hourRangeLabel={formatHourRange(selectedHour)}
            clipIndex={clipIndex}
            totalClips={totalClips}
          />
        )}
        <View
          style={[
            styles.videoContainer,
            // Cap the box by height and by the width that keeps the 16:9 box
            // within that height (capHeight * 16/9). Without the width cap, a
            // wide viewport makes the aspect-ratio-derived height exceed the
            // height cap, and overflow:hidden then crops the bottom of the clip.
            // Capping width instead shrinks the box uniformly so the whole frame
            // stays contained. capHeight is the caller override (Dumping Search
            // passes its measured available area) or 40% of the window height.
            isFullscreen || isLandscape ? null : (
              {
                maxHeight: maxHeightOverride ?? screenHeight * 0.4,
                maxWidth: (maxHeightOverride ?? screenHeight * 0.4) * (16 / 9),
              }
            ),
          ]}
        >
          {!videoLoaded && (
            <>
              {clip.thumbnailUrl && <VideoThumbnailOverlay thumbnailUrl={clip.thumbnailUrl} />}
              <VideoLoadingOverlay />
            </>
          )}

          <VideoPlayerView
            ref={videoViewRef}
            player={player}
            isFullscreen={isFullscreen}
            isLandscape={isLandscape}
            onTap={handleVideoTap}
            onFullscreenEnter={handleFullscreenEnter}
            onFullscreenExit={handleFullscreenExit}
            onFirstFrameRender={handleFirstFrameRender}
          />

          {/* Video controls overlay with fade animation */}
          <VideoControlsOverlay
            isPlaying={isPlaying}
            isEnded={isEnded}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            controlsVisible={controlsVisible}
            isFullscreen={isFullscreen}
            animatedStyle={controlsAnimatedStyle}
            onPrevious={onPrevious}
            onNext={onNext}
            onPlayPause={handlePlayPause}
          />

          {/* Fullscreen/Exit fullscreen button - bottom right with fade animation */}
          <AnimatedFullscreenButton
            isFullscreen={isFullscreen}
            controlsVisible={controlsVisible}
            animatedStyle={controlsAnimatedStyle}
            onPress={handleFullscreen}
          />

          {/* Mute/Unmute button - bottom left with fade animation (mirrors fullscreen) */}
          <AnimatedMuteButton
            isMuted={isMuted}
            isFullscreen={isFullscreen}
            controlsVisible={controlsVisible}
            animatedStyle={controlsAnimatedStyle}
            onPress={handleMuteToggle}
          />

          {/* Exit fullscreen button overlay - always visible when in fullscreen */}
          {isFullscreen && <ExitFullscreenButton onPress={handleFullscreen} />}

          {/* Progress bar at the bottom */}
          {!isFullscreen && !isLandscape && <VideoProgressBar currentTime={currentTime} duration={duration} />}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
  },
  videoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    position: "relative",
    alignSelf: "center",
    overflow: "hidden",
  },
  loadingContainer: {
    aspectRatio: 16 / 9,
    alignSelf: "center",
    borderRadius: theme.spacing.md,
    backgroundColor: "#64748B14",
  },
}));
